import type { BoxProps } from '@hover/blueprint';
import { Box, Icon, motion, useTheme } from '@hover/blueprint';
import { iconPlus } from '@hover/icons';
import React from 'react';

import {
  useHomeownerAppContext,
  useScrollToImage,
} from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import {
  Selector,
  SelectorItem,
} from 'src/shared/components/Selector/Selector';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import { useHasSettled } from '../../Favorites/LoggedInFavorites';
import type { ImageKindType } from '../LoggedInHome';

interface HomeButtonsBarProps extends BoxProps {
  handleUploadClick: () => void;
  selectedButton: ImageKindType;
  setSelectedButton: (value: ImageKindType) => void;
}

const HomeButtonsBar = ({
  handleUploadClick,
  selectedButton,
  setSelectedButton,
  ...props
}: HomeButtonsBarProps) => {
  const { trackingData } = useHomeownerAppContext();
  const [scrollToId] = useScrollToImage();

  const { space } = useTheme();
  const [hasSettled, settleRef] = useHasSettled();
  const willScroll = !!scrollToId;
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const MotionBox = motion(Box);

  const designIdeasText = t('home.designIdeas');
  const samplesText = t('home.samples');

  const radioOptions = [
    {
      label: designIdeasText,
      value: 'designIdeas',
    },
    {
      label: samplesText,
      value: 'samples',
    },
  ];

  return (
    <Box gap={100} {...props}>
      <MotionBox
        alignItems={{ tablet: 'center' }}
        animate={hasSettled ? { opacity: 1 } : undefined}
        display={{ base: 'flex' }}
        flexDir="column"
        initial={willScroll ? { opacity: 0 } : false}
        ref={settleRef}
        width="100%"
      >
        <Selector
          arrowLeft={0}
          arrowRight={0}
          background="neutral.0"
          defaultValue="1"
          display={{ base: 'flex', tablet: 'none' }}
          height="100%"
          onChange={(value: ImageKindType) => {
            setSelectedButton(value);
            buttonPressed({
              ...trackingData,
              button_location: 'top-main-page',
              button_text: value === 'samples' ? samplesText : designIdeasText,
              track_id: `open-${value}-home-tab`,
            });
          }}
          overflowDistanceX={{ base: space[500], tablet: space[200] }}
          overflowDistanceY={space[200]}
          paddingX={0}
          value={selectedButton}
          width="100%"
        >
          <Box gap={200} width={900}>
            {radioOptions.map(({ label, value }) => (
              <SelectorItem
                height="100%"
                key={value}
                value={value ?? undefined}
              >
                {label}
              </SelectorItem>
            ))}
          </Box>
          <Box>
            <Button
              display={{ tablet: 'none' }}
              height={350}
              ml="auto"
              name="Add photo"
              onClick={handleUploadClick}
              optionalTrackingProperties={{
                ...trackingData,
                button_location: 'top-main-page',
                button_text: '+',
                track_id: 'add-photo-home-tab-plus-button',
              }}
              shape="circle"
              variant="secondary"
              width={350}
            >
              <Icon icon={iconPlus} />
            </Button>
          </Box>
        </Selector>
      </MotionBox>
      <Button
        display={{ base: 'none', tablet: 'block' }}
        iconBefore={iconPlus}
        onClick={handleUploadClick}
        optionalTrackingProperties={{
          ...trackingData,
          button_location: 'top-main-page',
          track_id: 'add-photo-home-tab-plus-button',
        }}
      >
        {t('home.addPhoto')}
      </Button>
    </Box>
  );
};

export { HomeButtonsBar };
